import { useEffect, useRef } from 'react';

interface Beacon {
  x: number;
  y: number;
  opacity: number;
  lastHit: number;
}

export const SonarAnimation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const beaconsRef = useRef<Beacon[]>([]);
  const angleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initBeacons();
    };

    const initBeacons = () => {
      const beaconCount = Math.floor((canvas.width * canvas.height) / 40000);
      beaconsRef.current = [];
      
      for (let i = 0; i < beaconCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * Math.min(canvas.width, canvas.height) * 0.45 + 50;
        beaconsRef.current.push({
          x: canvas.width / 2 + Math.cos(angle) * distance,
          y: canvas.height / 2 + Math.sin(angle) * distance,
          opacity: 0,
          lastHit: 0
        });
      }
    };

    const draw = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxRadius = Math.min(canvas.width, canvas.height) * 0.45;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw concentric rings
      for (let i = 1; i <= 5; i++) {
        const radius = (maxRadius / 5) * i;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(262, 83%, 53%, ${0.08 - i * 0.01})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Draw sweep line with gradient
      const sweepAngle = angleRef.current;
      const sweepArc = Math.PI / 3; // 60 degrees

      const gradient = ctx.createConicGradient(sweepAngle - sweepArc, centerX, centerY);
      gradient.addColorStop(0, 'hsla(187, 85%, 43%, 0)');
      gradient.addColorStop(0.5, 'hsla(187, 85%, 43%, 0.15)');
      gradient.addColorStop(1, 'hsla(187, 85%, 43%, 0.3)');

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, maxRadius, sweepAngle - sweepArc, sweepAngle);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw sweep line
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + Math.cos(sweepAngle) * maxRadius,
        centerY + Math.sin(sweepAngle) * maxRadius
      );
      ctx.strokeStyle = 'hsla(187, 85%, 43%, 0.6)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Update and draw beacons
      beaconsRef.current.forEach((beacon) => {
        const beaconAngle = Math.atan2(beacon.y - centerY, beacon.x - centerX);
        let normalizedBeaconAngle = beaconAngle < 0 ? beaconAngle + Math.PI * 2 : beaconAngle;
        let normalizedSweepAngle = sweepAngle % (Math.PI * 2);
        if (normalizedSweepAngle < 0) normalizedSweepAngle += Math.PI * 2;

        const angleDiff = Math.abs(normalizedBeaconAngle - normalizedSweepAngle);
        const isHit = angleDiff < 0.1 || angleDiff > Math.PI * 2 - 0.1;

        if (isHit && Date.now() - beacon.lastHit > 500) {
          beacon.opacity = 1;
          beacon.lastHit = Date.now();
        }

        // Fade out
        beacon.opacity = Math.max(0, beacon.opacity - 0.008);

        if (beacon.opacity > 0) {
          // Draw glow
          const glowGradient = ctx.createRadialGradient(
            beacon.x, beacon.y, 0,
            beacon.x, beacon.y, 20
          );
          glowGradient.addColorStop(0, `hsla(187, 85%, 43%, ${beacon.opacity * 0.5})`);
          glowGradient.addColorStop(1, 'hsla(187, 85%, 43%, 0)');
          
          ctx.beginPath();
          ctx.arc(beacon.x, beacon.y, 20, 0, Math.PI * 2);
          ctx.fillStyle = glowGradient;
          ctx.fill();

          // Draw beacon dot
          ctx.beginPath();
          ctx.arc(beacon.x, beacon.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(187, 85%, 53%, ${beacon.opacity})`;
          ctx.fill();
        }
      });

      // Draw center dot
      const centerGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 30);
      centerGlow.addColorStop(0, 'hsla(262, 83%, 53%, 0.6)');
      centerGlow.addColorStop(1, 'hsla(262, 83%, 53%, 0)');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, 30, 0, Math.PI * 2);
      ctx.fillStyle = centerGlow;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(centerX, centerY, 6, 0, Math.PI * 2);
      ctx.fillStyle = 'hsl(262, 83%, 53%)';
      ctx.fill();

      // Update angle (~4.5 second rotation)
      angleRef.current += 0.023;

      animationRef.current = requestAnimationFrame(draw);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ 
        mixBlendMode: 'screen',
        opacity: 0.6
      }}
    />
  );
};
